/**
 * BlueHQ home page — where everyone lands when they sign in (deputy app and office):
 * important announcements, what needs attention, upcoming events, messages, announcements and this week's numbers.
 */
import { useState } from 'react';
import { api } from '../shared/api.ts';
import { useAuth } from '../shared/auth.tsx';
import { useRouter } from '../shared/router.tsx';
import { Icon } from '../shared/icons.tsx';
import { ErrorBox, Notice, Spinner, useAction, useLoad } from '../shared/ui.tsx';
import { ago, at } from '../ops/common.tsx';
import { AskBox } from './AskBox.tsx';
import { AlertsCard, PassOnCard } from './HomeExtras.tsx';
import { isBlueHQ } from '../shared/site.ts';

const greet = () => {
  const h = new Date().getHours();
  return h < 12 ? 'Good morning' : h < 17 ? 'Good afternoon' : 'Good evening';
};
const tel = (p: string) => `tel:${p.replace(/[^\d+]/g, '')}`;
const dayChip = (iso: string) => {
  const d = new Date(iso);
  const today = new Date();
  const tmr = new Date(Date.now() + 86_400_000);
  if (d.toDateString() === today.toDateString()) return 'Today';
  if (d.toDateString() === tmr.toDateString()) return 'Tomorrow';
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
};
const timeOf = (iso: string) => (/T12:00:00$/.test(iso) ? '' : new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }));

type Shortcut = { key: string; label: string; icon: string; to: string; tone?: string; abs?: boolean; show: boolean; def: boolean };

/** Every shortcut this person can have, in the default order; `def` = on Home until they customize. */
function shortcuts(can: (p: string) => boolean, civil: boolean, court: boolean): Shortcut[] {
  const all: Omit<Shortcut, 'show'>[] & { cond?: boolean }[] = [];
  const add = (key: string, label: string, icon: string, to: string, cond: boolean, def = true, extra: Partial<Shortcut> = {}) => all.push({ key, label, icon, to, def, ...extra, cond } as any);
  add('serve', 'Serve mode', 'route', '/field', civil, true, { abs: true, tone: 'serve' });
  add('critical', 'Critical notice', 'attention', '/pio/notices/new', !court, true, { tone: 'bad' });
  add('civil-papers', 'Civil papers', 'papers', '/civil-papers', can('portal.use'));
  add('evidence', 'Evidence room', 'evidence', '/evidence', can('evidence.manage'));
  add('court-orders', 'Court orders', 'gavel', '/evidence?tab=court', can('evidence.manage'));
  add('incident', 'New incident', 'report', '/reports?new=incident', can('reports.write'));
  add('ticket', 'Log a ticket', 'pen', '/tickets/new', can('reports.write'));
  add('tow', 'Tow report', 'car', '/tows/new', can('reports.write'));
  add('intel', 'Log what I saw', 'search', '/intel/new', can('reports.write'));
  add('photos', 'Upload photos', 'upload', '/photos', can('reports.write'));
  add('warrant-request', 'Request a warrant', 'gavel', '/warrants/request', can('warrants.request'));
  add('warrant-check', 'Warrant check', 'search', '/warrants', can('warrants.view'));
  add('message', 'New message', 'mail', '/messages/new', true);
  add('bolo', 'Put out a BOLO', 'bolo', '/bolos/new', !court && (can('reports.write') || can('reports.approve') || can('dispatch.view')), false);
  add('bolos', 'BOLOs', 'bolo', '/bolos', !court, false);
  add('events', 'Events', 'calendar', '/events', !court, false);
  add('forms', 'Forms', 'clipboard', '/forms', can('reports.write'), false);
  add('arrests', 'Recent arrests', 'arrest', '/arrests', can('reports.write') || can('reports.read') || can('dispatch.view'), false);
  add('my-cases', 'My cases', 'route', '/cases', can('reports.write'), false);
  add('schedule', 'Schedule', 'calendar', '/schedule', !court, false);
  add('activity', 'My activity', 'chart', '/stats', can('attempts.record') || can('reports.write'), false);
  add('messages', 'Messages', 'mail', '/messages', true, false);
  return (all as any[]).filter((x) => x.cond).map(({ cond: _c, ...x }) => ({ ...x, show: x.def }));
}

export function DeputyHome({ online = true }: { online?: boolean }) {
  const { me, can } = useAuth();
  const { navigate } = useRouter();
  const r = useLoad(() => (online ? api.get<any>('/api/me/landing') : Promise.resolve(null)), [online]);
  const prefs = useLoad(() => (online ? api.get<any>('/api/me/prefs').catch(() => null) : Promise.resolve(null)), [online]);
  const a = useAction();
  const [posting, setPosting] = useState(false);
  const [editing, setEditing] = useState<string[] | null>(null);
  const go = (p: string) => navigate(at(p));
  const first = (me?.user.displayName ?? '').replace(/^(Deputy|Dep\.?|Sgt\.?|Lt\.?|Cpl\.?|Capt\.?|Investigator|Dispatcher|Judge)\s+/i, '').split(' ')[0];
  // The court (magistrates) and the solicitor's office don't get the deputy tools or the search bar.
  const court = ['magistrate', 'solicitor'].includes(me?.user.role ?? '');
  const d = r.data;
  const catalog = shortcuts(can, !!d?.civil, court);
  const saved: string[] | null = prefs.data?.homeQuick ?? null;
  const order = editing ?? saved ?? catalog.filter((x) => x.def).map((x) => x.key);
  const byKey = new Map(catalog.map((x) => [x.key, x]));
  const quick = order.map((k) => byKey.get(k)).filter(Boolean) as Shortcut[];
  // Serve mode shows up for civil deputies even if they saved their list before it existed.
  if (!editing && saved && d?.civil && !saved.includes('serve') && !saved.includes('-serve') && byKey.get('serve')) quick.unshift(byKey.get('serve')!);
  const hidden = catalog.filter((x) => !order.includes(x.key));
  const important = (d?.announcements ?? []).filter((x: any) => x.important && !x.acked);
  const other = (d?.announcements ?? []).filter((x: any) => !(x.important && !x.acked));
  const ack = (id: string) => a.run(async () => (await api.post(`/api/announcements/${id}/ack`), r.reload()));
  const move = (i: number, dir: -1 | 1) => {
    const n = [...order];
    const j = i + dir;
    if (j < 0 || j >= n.length) return;
    [n[i], n[j]] = [n[j]!, n[i]!];
    setEditing(n);
  };
  const saveQuick = (list: string[] | null) =>
    a.run(async () => {
      // Remember that serve mode was taken off on purpose.
      const out = list && d?.civil && !list.includes('serve') ? [...list, '-serve'] : list;
      await api.post('/api/me/prefs', { homeQuick: out });
      setEditing(null);
      prefs.reload();
    });
  return (
    <div className="stack home dh">
      <div className="dh-top">
        <div className="dh-hello">
          <h1>
            {greet()}
            {first ? `, ${first}` : ''}
          </h1>
          <span className="muted">{new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}</span>
        </div>
        {d?.onCall?.length > 0 && (
          <div className="dh-oncall">
            <span className="small muted">On-call magistrate</span>
            <strong>{d.onCall[0].name}</strong>
            {d.onCall[0].phone && (
              <a href={tel(d.onCall[0].phone)} className="small">
                {d.onCall[0].phone}
              </a>
            )}
          </div>
        )}
      </div>
      {online && !court && <AskBox />}
      {!online && <Notice tone="warn">You’re offline. Your home page updates when you’re back online.</Notice>}
      <ErrorBox error={r.error ?? a.error} />

      {important.map((x: any) => (
        <div key={x.id} className="dh-important" role="alert">
          <div>
            <span className="dh-imp-tag">Important</span>
            <strong>{x.title}</strong>
            {x.body && <p>{x.body}</p>}
            <span className="small muted">
              {x.posted_by_name} · {ago(x.posted_at)}
              {x.event_at ? ` · ${dayChip(x.event_at)} ${timeOf(x.event_at)}${x.event_place ? `, ${x.event_place}` : ''}` : ''}
            </span>
          </div>
          <button className="btn primary" disabled={a.busy} onClick={() => ack(x.id)}>
            Got it
          </button>
        </div>
      ))}

      <div className={`dh-quick${editing ? ' editing' : ''}`}>
        {quick.map((q, i) => (
          <div key={q.key} className="dh-q-wrap">
            <button type="button" className={`dh-q${q.tone ? ` ${q.tone}` : ''}`} disabled={!!editing} onClick={() => (q.abs ? navigate(q.to) : go(q.to))}>
              <Icon name={q.icon} size={22} />
              <span>{q.label}</span>
            </button>
            {editing && (
              <div className="dh-q-edit">
                <button type="button" aria-label={`Move ${q.label} earlier`} disabled={i === 0} onClick={() => move(i, -1)}>
                  ‹
                </button>
                <button type="button" aria-label={`Remove ${q.label}`} className="x" onClick={() => setEditing(order.filter((k) => k !== q.key))}>
                  ✕
                </button>
                <button type="button" aria-label={`Move ${q.label} later`} disabled={i === quick.length - 1} onClick={() => move(i, 1)}>
                  ›
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
      {online && !court && (
        <div className="dh-q-tools">
          {editing ? (
            <div className="stack sm">
              {hidden.length > 0 && (
                <div className="dh-q-add">
                  <span className="small strong">Add a shortcut:</span>
                  {hidden.map((x) => (
                    <button key={x.key} type="button" className="btn sm" onClick={() => setEditing([...order, x.key])}>
                      + {x.label}
                    </button>
                  ))}
                </div>
              )}
              <div className="row gap-sm wrap">
                <button type="button" className="btn primary sm" disabled={a.busy} onClick={() => saveQuick(order)}>
                  Save shortcuts
                </button>
                <button type="button" className="btn sm" onClick={() => setEditing(null)}>
                  Cancel
                </button>
                <button type="button" className="btn ghost sm" disabled={a.busy} onClick={() => saveQuick(null)}>
                  Back to the standard set
                </button>
              </div>
            </div>
          ) : (
            <button type="button" className="btn link small" onClick={() => setEditing(quick.map((x) => x.key))}>
              Customize shortcuts
            </button>
          )}
        </div>
      )}

      {online && !d && !r.error ? (
        <Spinner />
      ) : d ? (
        <div className="dh-cols">
          <section aria-labelledby="dh-att" className="dh-main">
            {isBlueHQ() && <AlertsCard />}
            <h2 id="dh-att" className="dh-h">
              Needs your attention
            </h2>
            {d.attention.length === 0 ? (
              <div className="card dh-clear">
                <Icon name="check" size={26} />
                <strong>You’re all caught up.</strong>
                <span className="muted">Nothing is waiting on you right now.</span>
              </div>
            ) : (
              <div className="dh-groups">
                {d.attention.map((g: any) => (
                  <div key={g.key} className={`card dh-g ${g.tone}`}>
                    <button type="button" className="dh-g-h" onClick={() => go(g.link)}>
                      <span className="dh-n">{g.n}</span>
                      <strong>{g.title}</strong>
                      <span className="dh-more" aria-hidden>
                        ›
                      </span>
                    </button>
                    {g.items.length > 0 && (
                      <ul>
                        {g.items.map((it: any, i: number) => (
                          <li key={i}>
                            <button type="button" onClick={() => go(it.link ?? g.link)}>
                              <span>
                                <span className="dh-it">{it.title}</span>
                                {it.sub && <span className="small muted"> · {it.sub}</span>}
                              </span>
                              {it.when && <span className="small muted dh-when">{new Date(it.when) > new Date() ? `${dayChip(it.when)} ${timeOf(it.when)}` : ago(it.when)}</span>}
                            </button>
                          </li>
                        ))}
                        {g.n > g.items.length && (
                          <li>
                            <button type="button" className="small" onClick={() => go(g.link)}>
                              {g.n - g.items.length} more…
                            </button>
                          </li>
                        )}
                      </ul>
                    )}
                  </div>
                ))}
              </div>
            )}

            {isBlueHQ() && !court && <PassOnCard />}
          </section>

          <aside className="dh-side">
            <HomeCalendar events={d.events ?? []} go={go} />

            <div className="card dh-card">
              <div className="row between">
                <h2 className="dh-h">Messages</h2>
                <button type="button" className="btn link" onClick={() => go('/messages')}>
                  All ›
                </button>
              </div>
              {d.messages.length === 0 ? (
                <p className="muted small">No messages.</p>
              ) : (
                <ul className="dh-msgs">
                  {d.messages.map((m: any) => (
                    <li key={m.id} className={m.unread ? 'unread' : ''}>
                      <button type="button" onClick={() => go(`/messages/${m.id}`)}>
                        <span>
                          <span className="dh-it">{m.subject}</span>
                          <span className="small muted">
                            {' '}
                            · {m.from_name}
                            {m.needs_reply ? ' · reply needed' : ''}
                          </span>
                        </span>
                        <span className="small muted dh-when">{ago(m.created_at)}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="card dh-card">
              <div className="row between">
                <h2 className="dh-h">Announcements</h2>
                {d.canPost && !posting && (
                  <button type="button" className="btn link" onClick={() => setPosting(true)}>
                    + Post
                  </button>
                )}
              </div>
              {posting && <PostAnnouncement schools={d.schools ?? []} canPostAll={d.canPostAll !== false} onDone={() => (setPosting(false), r.reload())} onCancel={() => setPosting(false)} />}
              {other.length === 0 && !posting ? (
                <p className="muted small">No announcements.</p>
              ) : (
                <ul className="dh-ann">
                  {other.map((x: any) => (
                    <li key={x.id}>
                      {x.school_name && <span className="dh-school-tag">School · {x.school_name}</span>}
                      <strong>{x.title}</strong>
                      {x.important && <span className="dh-imp-tag sm">Important</span>}
                      {x.body && <p className="small">{x.body}</p>}
                      <span className="small muted">
                        {x.posted_by_name} · {ago(x.posted_at)}
                        {x.event_at ? ` · ${dayChip(x.event_at)} ${timeOf(x.event_at)}${x.event_place ? `, ${x.event_place}` : ''}` : ''}
                        {d.canPost && x.posted_by === me?.user.id ? ` · ${x.acks} read` : ''}
                      </span>
                      {(x.posted_by === me?.user.id || can('admin.config')) && (
                        <button type="button" className="btn link small" disabled={a.busy} onClick={() => window.confirm('Take this announcement down?') && a.run(async () => (await api.post(`/api/announcements/${x.id}/remove`), r.reload()))}>
                          Take down
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </aside>
        </div>
      ) : null}
    </div>
  );
}

const EV_ICON: Record<string, string> = { court: 'gavel', setout: 'door', event: 'calendar', schedule: 'calendar', training: 'book', escort: 'car', detail: 'calendar' };
const ymd = (d: Date) => d.toLocaleDateString('en-CA');

/** Two weeks at a glance: a dot on each day with something on it; tap a day to see just that day. */
function HomeCalendar({ events, go }: { events: any[]; go: (p: string) => void }) {
  const [pick, setPick] = useState<string | null>(null);
  const today = new Date();
  const start = new Date(today);
  start.setDate(today.getDate() - today.getDay()); // Sunday of this week
  const days = Array.from({ length: 14 }, (_, i) => {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    return d;
  });
  const keyOf = (e: any) => ymd(new Date(e.at));
  const count = new Map<string, number>();
  for (const e of events) count.set(keyOf(e), (count.get(keyOf(e)) ?? 0) + 1);
  const shown = pick ? events.filter((e) => keyOf(e) === pick) : events;
  return (
    <div className="card dh-card">
      <div className="row between">
        <h2 className="dh-h">Calendar</h2>
        <button type="button" className="btn link" onClick={() => go('/schedule')}>
          Full schedule ›
        </button>
      </div>
      <div className="hc-grid" role="grid" aria-label="Next two weeks">
        {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((x, i) => (
          <span key={i} className="hc-dow" aria-hidden>
            {x}
          </span>
        ))}
        {days.map((d) => {
          const k = ymd(d);
          const n = count.get(k) ?? 0;
          const past = k < ymd(today);
          return (
            <button
              key={k}
              type="button"
              role="gridcell"
              className={`hc-day${k === ymd(today) ? ' today' : ''}${pick === k ? ' on' : ''}${past ? ' past' : ''}`}
              aria-label={`${d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}: ${n ? `${n} thing${n === 1 ? '' : 's'}` : 'nothing'}`}
              aria-pressed={pick === k}
              onClick={() => setPick(pick === k ? null : k)}
            >
              <span>{d.getDate()}</span>
              {n > 0 && <i className="hc-dot" aria-hidden />}
            </button>
          );
        })}
      </div>
      {shown.length === 0 ? (
        <p className="muted small">{pick ? 'Nothing that day.' : 'Nothing on your calendar in the next two weeks.'}</p>
      ) : (
        <ul className="dh-ev">
          {shown.map((e: any, i: number) => (
            <li key={i}>
              <button type="button" disabled={!e.link} onClick={() => e.link && go(e.link)}>
                <span className="dh-ev-day">
                  <strong>{dayChip(e.at)}</strong>
                  <span className="small">{timeOf(e.at)}</span>
                </span>
                <Icon name={EV_ICON[e.kind] ?? 'calendar'} size={18} />
                <span>
                  <span className="dh-it">{e.title}</span>
                  {e.sub && <span className="small muted"> · {e.sub}</span>}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function PostAnnouncement({ onDone, onCancel, schools, canPostAll }: { onDone: () => void; onCancel: () => void; schools: { id: string; name: string }[]; canPostAll: boolean }) {
  const [f, setF] = useState({ title: '', body: '', important: false, eventAt: '', eventPlace: '', expiresOn: '', schoolId: !canPostAll && schools.length === 1 ? schools[0]!.id : '' });
  const a = useAction();
  return (
    <form
      className="stack dh-post"
      onSubmit={(e) => {
        e.preventDefault();
        a.run(async () => {
          await api.post('/api/announcements', {
            title: f.title,
            body: f.body,
            important: f.important,
            eventAt: f.eventAt ? new Date(f.eventAt).toISOString() : undefined,
            eventPlace: f.eventPlace || undefined,
            expiresAt: f.expiresOn ? new Date(`${f.expiresOn}T23:59:00`).toISOString() : undefined,
            schoolId: f.schoolId || undefined,
          });
          onDone();
        });
      }}
    >
      {schools.length > 0 && (
        <label>
          School
          <select value={f.schoolId} onChange={(e) => setF({ ...f, schoolId: e.target.value })} required={!canPostAll}>
            {canPostAll ? <option value="">Not about a school (everyone)</option> : <option value="">Pick the school…</option>}
            {schools.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
          <span className="hint">School announcements show on everyone’s Home with the school’s name (lockdowns, events, pickup changes, threats being looked into).</span>
        </label>
      )}
      <label>
        Title
        <input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} required maxLength={160} />
      </label>
      <label>
        Details
        <textarea rows={3} value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} />
      </label>
      <label>
        Date and time (if it’s an event)
        <input type="datetime-local" value={f.eventAt} onChange={(e) => setF({ ...f, eventAt: e.target.value })} />
      </label>
      {f.eventAt && (
        <label>
          Where
          <input value={f.eventPlace} onChange={(e) => setF({ ...f, eventPlace: e.target.value })} />
        </label>
      )}
      <label>
        Show until
        <input type="date" value={f.expiresOn} onChange={(e) => setF({ ...f, expiresOn: e.target.value })} />
        <span className="hint">Blank: two weeks (or the day after the event).</span>
      </label>
      <label className="check">
        <input type="checkbox" checked={f.important} onChange={(e) => setF({ ...f, important: e.target.checked })} /> Important: everyone has to tap “Got it”
      </label>
      <ErrorBox error={a.error} />
      <div className="row gap-sm">
        <button className="btn primary" disabled={a.busy}>
          Post
        </button>
        <button type="button" className="btn ghost" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}
